import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Center,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr
} from "@chakra-ui/react";

import { Paginated } from "@/jenesys-types/data/common";
import { TeamMember } from "@/jenesys-types/jenesys-entities/config";

import Barrier from "@/components/common/permission/Barrier";
import { Paginate } from "@/components/common/table/Paginate";

interface TeamConnectionsProps {
  team: Paginated<TeamMember>;
  teamPage: number;
  isWhatsapp: boolean;
  platform: string | undefined;
  selectedMember: TeamMember | null;
  isFetchingTeam: boolean;
  isInvitingMember: boolean;
  whatsappDisclosure: any;
  resendInviteDisclosure: any;
  disconnectMemberDisclosure: any;
  triggerDisconnectPendingInvite: (member: TeamMember) => void;
  setSelectedMember: (member: TeamMember | null) => void;
  handleTeamPageClick: (selected: any) => void;
  handleSubmitAddMember: (member: TeamMember) => void;
}

export const TeamConnections: React.FC<TeamConnectionsProps> = ({
  team,
  teamPage,
  isWhatsapp,
  platform,
  selectedMember,
  isFetchingTeam,
  isInvitingMember,
  whatsappDisclosure,
  resendInviteDisclosure,
  disconnectMemberDisclosure,
  triggerDisconnectPendingInvite,
  setSelectedMember,
  handleTeamPageClick,
  handleSubmitAddMember
}) => (
  <Accordion mt={12} defaultIndex={[0]} allowMultiple>
    <AccordionItem border={0}>
      <h2>
        <AccordionButton>
          <Box as="span" flex="1" textAlign="left">
            <Text fontWeight={600}>Team members</Text>
          </Box>
          <AccordionIcon />
        </AccordionButton>
      </h2>
      <AccordionPanel pb={4}>
        <Box>
          <TableContainer
            border="1px solid"
            borderColor="blackAlpha.300"
            rounded={6}
            pt={6}
            pb={6}
            px={8}
            mt={4}
            flexShrink={0}
          >
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th
                    pl={0}
                    color="black"
                    textTransform="capitalize"
                    fontWeight={600}
                  >
                    Team member
                  </Th>
                  {isWhatsapp && (
                    <Th
                      color="black"
                      textTransform="capitalize"
                      fontWeight={600}
                    >
                      WhatsApp Number
                    </Th>
                  )}
                  <Th color="black" textTransform="capitalize" fontWeight={600}>
                    Status
                  </Th>
                  <Barrier show when="company.connections.invite">
                    <Th
                      color="black"
                      textTransform="capitalize"
                      fontWeight={600}
                    />
                  </Barrier>
                </Tr>
              </Thead>
              <Tbody>
                {team.items.map((member: TeamMember) => {
                  const connection = isWhatsapp
                    ? member.whatsapp_connection
                    : member.connections.find(
                        (conn) => conn.platform === platform
                      );

                  const isConnected = connection?.status === "connected";
                  const isPending =
                    connection?.status === "pending" ||
                    connection?.status === "invited";

                  return (
                    <Tr key={member.id}>
                      <Td pl={0} fontWeight={500} py={4}>
                        {member.user.first_name} {member.user.last_name}
                      </Td>
                      {isWhatsapp && (
                        <Td fontWeight={500} py={4}>
                          {member.whatsapp_connection?.number || "-"}
                        </Td>
                      )}
                      <Td fontWeight={500} py={4}>
                        {!isConnected && !isPending && "Not Connected"}
                        {isConnected && "Connected"}
                        {isPending && "Pending"}
                      </Td>
                      <Barrier show when="company.connections.invite">
                        <Td fontWeight={500} py={4}>
                          {isConnected && (
                            <Button
                              onClick={() => {
                                setSelectedMember(member);
                                disconnectMemberDisclosure.onOpen();
                              }}
                              variant="link"
                              colorScheme="red"
                              size="sm"
                              fontWeight={500}
                            >
                              Disconnect
                            </Button>
                          )}

                          {isWhatsapp && !isConnected && isPending && (
                            <>
                              <Button
                                isLoading={
                                  isInvitingMember &&
                                  member.id === selectedMember?.id
                                }
                                onClick={() => {
                                  setSelectedMember(member);
                                  resendInviteDisclosure.onOpen();
                                }}
                                variant="link"
                                colorScheme="blue"
                                size="sm"
                                fontWeight={500}
                                mr={2}
                              >
                                Resend Invite
                              </Button>
                              <Button
                                onClick={() => {
                                  triggerDisconnectPendingInvite(member);
                                }}
                                variant="link"
                                colorScheme="red"
                                size="sm"
                                fontWeight={500}
                              >
                                Cancel
                              </Button>
                            </>
                          )}

                          {!isConnected && !isPending && (
                            <Button
                              isLoading={
                                isInvitingMember &&
                                member.id === selectedMember?.id
                              }
                              onClick={() => {
                                setSelectedMember(member);
                                if (isWhatsapp) {
                                  whatsappDisclosure.onOpen();
                                } else {
                                  handleSubmitAddMember(member);
                                }
                              }}
                              variant="link"
                              colorScheme="blue"
                              size="sm"
                              fontWeight={500}
                            >
                              {isWhatsapp ? "Connect WhatsApp" : "Invite"}
                            </Button>
                          )}
                        </Td>
                      </Barrier>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>

            {isFetchingTeam && (
              <Center mt={10}>
                <Spinner color="primary.500" />
              </Center>
            )}

            <Box mt={6}>
              <Paginate
                page={teamPage}
                data={team}
                handlePageClick={handleTeamPageClick}
              />
            </Box>
          </TableContainer>
        </Box>
      </AccordionPanel>
    </AccordionItem>
  </Accordion>
);
